import React, {useEffect, useState} from "react";
import Api from "../../../../../core/services/api/api";
import useIsMounted from "../../../../hooks/use-is-mounted";
import {Col, Row} from "reactstrap";
import {Divider, Paper} from "@mui/material";
import SearchInput from "../../../base/input/search_input";
import TryAgain from "../../try-again";
import Button from "../../../base/button/button";
import {CSSTransition, TransitionGroup} from "react-transition-group";
import UpsertOrderNoteDialog from "../../../dialogs/upsert-order-note";
import {Add, Edit} from "@mui/icons-material";
import {createUUId} from "../../../../../core/services/utils/utils";
import IconButton from "../../../base/button/icon_button";
import DateUtils from "../../../../../core/services/date-utils";
import {ReactComponent as RemoveIcon} from "../../../../../assets/images/remove.svg";
import {ReactComponent as TypeIcon} from "../../../../../assets/images/order-notes/type.svg";
import {ReactComponent as AutoDefineIcon} from "../../../../../assets/images/order-notes/auto-define.svg";


export const OrderNoteAutoDefineTypes = {
    quote: "Quote",
    salesOrder: "Sales order",
    invoice: "Invoice",
}

const OrderInformationViewNotesSection = ({orderNo, loading: orderLoading, placeType, editable = true}) => {
    const [loading, setLoading] = useState(true)
    const [_notes, _setNotes] = useState([])
    const [notes, setNotes] = useState([])
    const [keywords, setKeywords] = useState('')
    const [selectedNote, setSelectedNote] = useState({note: null, open: false})
    const isMounted = useIsMounted()

    /**
     * Listens for the changes of orderLoading and with each change:
     * if its not loading anymore, fetches the order notes.
     */
    useEffect(() => {
        if (orderLoading) return
        getOrderNotes()
    }, [orderLoading])

    useEffect(() => {
        searchNotes(keywords)
    }, [_notes])


    /**
     * Gets the order notes of the specified order from the server and populates the order list.
     */
    const getOrderNotes = () => {
        setLoading(true)
        Api.getOrderNotes(orderNo).then((response) => {
            if (!isMounted()) return
            if (response?.resultFlag) {
                const orders = response.data?.map((e, index) => ({
                    ...e,
                    key: index + 1,
                    loading: false,
                })) ?? []
                _setNotes(orders)
                setNotes(orders)
            }
            setLoading(false)
        })
    }

    /**
     * Searches within the notes and filter the ones that would match the keywords.
     *
     * the properties used for searching:
     * 1. subject
     * 2. body
     * @param {string} keywords the filtering keywords.
     */
    const searchNotes = (keywords) => {
        keywords = keywords?.trim()
        setKeywords(keywords ?? '')
        if (!keywords?.trim()?.length) {
            setNotes(_notes)
            return
        }
        keywords = keywords?.toLowerCase()
        setNotes(_notes.filter(e =>
            e.subject?.trim()?.toLowerCase()?.includes(keywords) ||
            e.body?.trim()?.toLowerCase()?.includes(keywords)
        ))
    }

    /**
     * Removes a note from the specified order from the server and updates the internal lists if successful.
     * @param {any} note the note to be removed.
     */
    const removeNote = (note) => {
        setNotes(prevState => prevState?.map(e => e.key === note.key ? {...e, loading: true} : e))
        Api.removeOrderNote(note.id).then((response) => {
            if (!isMounted()) return
            if (response?.resultFlag) {
                _setNotes(prevState => prevState?.filter(e => e.key !== note.key))
                setNotes(prevState => prevState?.filter(e => e.key !== note.key))
            } else {
                setNotes(prevState => prevState?.map(e => e.key === note.key ? {...e, loading: true} : e))
            }
        })
    }

    /**
     * Opens the upsert dialog of the order notes.
     * @param {any | null} note the note to be updated. Null if a new note is to be created.
     */
    const onUpsertClicked = (note = null) => {
        setSelectedNote({open: true, note: note})
    }

    /**
     * Appends the newly create note to the list of notes.
     *
     * @param {any} note the created note.
     */
    const onCreateNote = (note) => {
        _setNotes(prevState => [...prevState, {...note, key: createUUId(true)}])
    }

    /**
     * Updates the note in the internal list.
     *
     * @param {any} note the updated note.
     */
    const onUpdateNote = (note) => {
        _setNotes(prevState => prevState?.map(e => e.key === note.key
            ? {
                ...e,
                ...note,
            }
            : e
        ))
    }

    return (
        <>
            <Row className='no-gutters w-100 align-items-start justify-content-center mt--3'>
                <Col xs={12} className={'d-flex justify-content-end'}>
                    {
                        editable &&
                        <Button
                            disabled={loading || orderLoading}
                            className='d-flex align-items-center justify-content-center mb-3'
                            variant={'outlined'}
                            color={'primary'}
                            onClick={() => onUpsertClicked()}
                        >
                            <Add className={'mr-2'}/>
                            <p>
                                Add a new Note
                            </p>
                        </Button>
                    }
                </Col>
                <Paper className='paper order-notes w-100 px-3 py-2 flex-column'>
                    <Col xs={12} className='px-3'>
                        <SearchInput
                            loading={loading || orderLoading}
                            autoSearch={true}
                            search={searchNotes}
                            label={'Search'}
                        />
                    </Col>
                    <Divider className='divider'/>
                    <div className='my-2 w-100'/>
                    <div className={'d-flex flex-wrap w-100 justify-content-center'}>
                        {
                            loading
                                ? <>
                                    {
                                        Array(6).fill(null).map((e, index) => (
                                            <div key={index} className={'loading order-note'}>
                                                <div/>
                                            </div>
                                        ))
                                    }
                                </>
                                : _notes?.length < 1
                                    ? <TryAgain
                                        onClick={() => onUpsertClicked()}
                                        text={"There is currently no notes for this order."}
                                        buttonText={'Create a note'}
                                        showButton={editable}
                                    />
                                    : notes?.length < 1
                                        ? <TryAgain
                                            onClick={() => searchNotes('')}
                                            text={"No notes found with the given keywords."}
                                            buttonText={'Remove filters'}
                                            showButton={true}
                                        />
                                        : <>
                                            <TransitionGroup className={'w-100 d-flex flex-wrap justify-content-center'}>
                                                {
                                                    notes?.map((note) => (
                                                        <CSSTransition
                                                            classNames='item'
                                                            key={note.key}
                                                            timeout={500}
                                                        >
                                                            <OrderNote
                                                                note={note}
                                                                editable={editable}
                                                                onEdit={() => onUpsertClicked(note)}
                                                                onRemove={() => removeNote(note)}
                                                            />
                                                        </CSSTransition>
                                                    ))
                                                }
                                            </TransitionGroup>
                                        </>
                        }
                    </div>
                </Paper>
            </Row>
            {
                editable &&
                <UpsertOrderNoteDialog
                    open={selectedNote.open}
                    setOpen={(open) => setSelectedNote(prevState => ({...prevState, open: open, note: null}))}
                    initialValues={selectedNote.note}
                    onAdd={onCreateNote}
                    onUpdate={onUpdateNote}
                    type={placeType}
                    orderNo={orderNo}
                />
            }
        </>
    )
}


const OrderNote = ({note, onEdit, onRemove, editable}) => {

    /**
     * Gets users confirmation before removing the current entity.
     */
    const getRemoveConfirmation = () => {
        window.partner?.openConfirmationDialog(
            <div className={'p-3'}>
                <p className={'text-sm text-color-2'}>
                    Are you sure you want to remove this note?
                </p>
            </div>,
            {
                maxWidth: 'sm'
            },
            (confirmed) => confirmed && onRemove(),
        );
    }

    return (
        <>
            <div className={'order-note'}>
                <div className={'d-flex align-items-center justify-content-end'}>
                    {
                        editable &&
                        <IconButton
                            onPressed={onEdit}
                            disabled={note?.loading}
                            color={'paper.secondary'}
                        >
                            <Edit className='icon normal'/>
                        </IconButton>
                    }
                    {
                        editable &&
                        <IconButton
                            onPressed={getRemoveConfirmation}
                            disabled={note?.loading}
                            color={'paper.error'}
                            className={'ml-2'}>
                            <RemoveIcon className='icon normal'/>
                        </IconButton>
                    }
                </div>
                <div className={'d-flex'}>
                    <p className={'subject'}>
                        {note?.subject ?? ''}
                    </p>
                </div>
                <Divider className='divider my-2'/>
                <div className={'d-flex align-items-center justify-content-between mt-1'}>
                    <p className={'category'}>
                        {note?.type?.type ?? ''}
                    </p>
                    <div className={'print-place'}>
                        <TypeIcon className={'mr-1'}/>
                        {note?.printPlace?.name ?? ''}
                    </div>
                </div>
                <div className={'d-flex align-items-center justify-content-between mt-1'}>
                    <p className={'date'}>
                        {DateUtils.serverToLocal(note?.lastModifiedDate ?? note?.createdDate).format(DateUtils.dateFormat)}
                    </p>
                    {
                        note?.isAutoDefine &&
                        <div className={'print-place'}>
                            <AutoDefineIcon className={'mr-1'}/>
                            Auto Define
                        </div>
                    }
                </div>
                <p className={'body'}>
                    {note?.body ?? ''}
                </p>
            </div>
        </>
    )
}


export default OrderInformationViewNotesSection;
